import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer,
} from 'recharts';
import { TrendingDown, TrendingUp, Minus, Scale, Plus, Activity } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { useTheme } from '@/contexts/ThemeContext';
import axios from 'axios';

// ─── BMI + Prediction Card ─────────────────────────────────────────────────────
const BmiPredictionCard = ({ history }) => {
  const tdeeData = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('portion_tdee') || '{}'); }
    catch { return {}; }
  }, []);

  const heightCm = parseFloat(tdeeData.height) || 0;
  const goal     = tdeeData.goal || '';

  const weightHistory = useMemo(
    () => history.filter(e => e.weight != null),
    [history]
  );

  const firstWeight = weightHistory[0]?.weight;
  const lastWeight  = weightHistory[weightHistory.length - 1]?.weight;

  const bmi = useMemo(() => {
    if (!heightCm || !lastWeight) return null;
    return parseFloat((lastWeight / Math.pow(heightCm / 100, 2)).toFixed(1));
  }, [heightCm, lastWeight]);

  const bmiInfo = useMemo(() => {
    if (!bmi) return null;
    let category, color;
    if (bmi < 18.5)     { category = 'Поднормено тегло';   color = '#60a5fa'; }
    else if (bmi < 25)  { category = 'Нормално тегло';      color = '#34d399'; }
    else if (bmi < 30)  { category = 'Наднормено тегло';    color = '#f59e0b'; }
    else                { category = 'Затлъстяване';         color = '#f43f5e'; }
    const pct = Math.min(100, Math.max(0, ((bmi - 15) / 25) * 100));
    return { bmi, category, color, pct };
  }, [bmi]);

  const weeklyRate = useMemo(() => {
    if (weightHistory.length < 2 || !firstWeight || !lastWeight) return null;
    const firstDate = new Date(weightHistory[0].date + 'T00:00:00');
    const lastDate  = new Date(weightHistory[weightHistory.length - 1].date + 'T00:00:00');
    const days = (lastDate - firstDate) / (1000 * 60 * 60 * 24);
    if (days < 6) return null; // need at least ~1 week of spread
    return parseFloat(((lastWeight - firstWeight) / days * 7).toFixed(2));
  }, [weightHistory, firstWeight, lastWeight]);

  if (!bmiInfo && weeklyRate === null) return null;

  const isLosing  = weeklyRate !== null && weeklyRate < 0;
  const isGaining = weeklyRate !== null && weeklyRate > 0;
  const rateColor = isLosing ? '#34d399' : isGaining ? '#fb923c' : '#94a3b8';
  const rateIcon  = isLosing ? TrendingDown : isGaining ? TrendingUp : Minus;
  const RateIcon  = rateIcon;

  const thirtyDayChange = weeklyRate !== null
    ? Math.abs(parseFloat((weeklyRate * 4.3).toFixed(1)))
    : null;
  const ninetyDayChange = weeklyRate !== null
    ? Math.abs(parseFloat((weeklyRate * 13).toFixed(1)))
    : null;

  const bothCards = bmiInfo && weeklyRate !== null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1 }}
      className={`mt-4 grid gap-3 ${bothCards ? 'sm:grid-cols-2' : 'grid-cols-1'}`}
    >
      {/* BMI Card */}
      {bmiInfo && (
        <Card className="rounded-2xl border border-gray-100 dark:border-slate-700" data-testid="bmi-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: `${bmiInfo.color}18` }}>
                <Activity style={{ color: bmiInfo.color }} className="w-4 h-4" strokeWidth={1.8} />
              </div>
              <p className="text-xs font-bold uppercase tracking-widest text-slate-400 dark:text-slate-500">
                ИТМ (BMI)
              </p>
            </div>
            <div className="flex items-end justify-between mb-2.5">
              <p className="text-3xl font-black text-gray-900 dark:text-gray-100 tabular-nums leading-none">
                {bmiInfo.bmi}
              </p>
              <span className="text-xs font-semibold px-2.5 py-1 rounded-full"
                style={{ backgroundColor: `${bmiInfo.color}18`, color: bmiInfo.color }}>
                {bmiInfo.category}
              </span>
            </div>
            <div className="h-2 bg-gray-100 dark:bg-slate-800 rounded-full overflow-hidden">
              <motion.div
                className="h-full rounded-full"
                style={{ backgroundColor: bmiInfo.color }}
                initial={{ width: 0 }}
                animate={{ width: `${bmiInfo.pct}%` }}
                transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              />
            </div>
            <div className="flex justify-between mt-1">
              <span className="text-[10px] text-slate-300 dark:text-slate-600">15</span>
              <span className="text-[10px] text-slate-300 dark:text-slate-600">25</span>
              <span className="text-[10px] text-slate-300 dark:text-slate-600">40+</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Prediction Card */}
      {weeklyRate !== null && (
        <Card className="rounded-2xl border border-gray-100 dark:border-slate-700" data-testid="prediction-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: `${rateColor}18` }}>
                <RateIcon style={{ color: rateColor }} className="w-4 h-4" strokeWidth={1.8} />
              </div>
              <p className="text-xs font-bold uppercase tracking-widest text-slate-400 dark:text-slate-500">
                Темп на промяна
              </p>
            </div>
            <p className="text-3xl font-black tabular-nums leading-none mb-0.5"
              style={{ color: rateColor }}>
              {weeklyRate > 0 ? '+' : ''}{weeklyRate} <span className="text-base font-semibold">кг</span>
            </p>
            <p className="text-xs text-slate-400 dark:text-slate-500 mb-3">седмично</p>
            <div className="space-y-1.5 pt-3 border-t border-gray-100 dark:border-slate-700/60">
              <div className="flex justify-between text-xs">
                <span className="text-slate-400 dark:text-slate-500">За 30 дни</span>
                <span className="font-semibold text-gray-700 dark:text-gray-300">
                  {isLosing ? '-' : isGaining ? '+' : '±'}{thirtyDayChange} кг
                </span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-slate-400 dark:text-slate-500">За 90 дни</span>
                <span className="font-semibold text-gray-700 dark:text-gray-300">
                  {isLosing ? '-' : isGaining ? '+' : '±'}{ninetyDayChange} кг
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </motion.div>
  );
};

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const METRIC_CONFIG = {
  weight:   { label: 'Тегло',           unit: 'кг',  color: '#34d399' },
  waist_cm: { label: 'Талия',           unit: 'см',  color: '#60a5fa' },
  chest_cm: { label: 'Гръдна обиколка', unit: 'см',  color: '#f472b6' },
  bicep_cm: { label: 'Ръка / Бицепс',  unit: 'см',  color: '#fb923c' },
  thigh_cm: { label: 'Крак / Бедро',   unit: 'см',  color: '#a78bfa' },
};

const CustomTooltip = ({ active, payload, label, unit }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white dark:bg-slate-800 border border-gray-100 dark:border-slate-700 rounded-xl px-4 py-3 shadow-xl">
      <p className="text-xs text-gray-400 dark:text-gray-500 mb-1">{label}</p>
      <p className="text-sm font-bold text-gray-900 dark:text-gray-100">
        {payload[0].value} {unit}
      </p>
    </div>
  );
};

export const ProgressSection = ({ refreshTrigger, onAddCheckin, planCreatedAt }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('weight');
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const loadHistory = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/progress/history`);
      setHistory(res.data.history || []);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadHistory(); }, [loadHistory, refreshTrigger]);

  // Filter to entries from this plan's period only
  const planStartDate = planCreatedAt ? planCreatedAt.substring(0, 10) : null;
  const filteredHistory = useMemo(
    () => planStartDate ? history.filter(e => e.date >= planStartDate) : history,
    [history, planStartDate]
  );

  // Which tabs have at least 1 data point
  const availableTabs = useMemo(() => {
    const tabs = ['weight'];
    ['waist_cm', 'chest_cm', 'bicep_cm', 'thigh_cm'].forEach((key) => {
      if (filteredHistory.some((e) => e[key] != null)) tabs.push(key);
    });
    return tabs;
  }, [filteredHistory]);

  // Reset active tab if it no longer has data
  useEffect(() => {
    if (!availableTabs.includes(activeTab)) setActiveTab('weight');
  }, [availableTabs, activeTab]);

  const activeCfg = METRIC_CONFIG[activeTab];

  // History entries that have a value for the current tab
  const activeHistory = useMemo(
    () => filteredHistory.filter((e) => e[activeTab] != null),
    [filteredHistory, activeTab]
  );

  const chartData = activeHistory.map((entry) => ({
    date: new Date(entry.date + 'T00:00:00').toLocaleDateString('bg-BG', {
      day: '2-digit',
      month: '2-digit',
    }),
    value: entry[activeTab],
  }));

  const firstVal = activeHistory[0]?.[activeTab];
  const lastVal  = activeHistory[activeHistory.length - 1]?.[activeTab];
  const change =
    firstVal != null && lastVal != null && activeHistory.length >= 2
      ? parseFloat((lastVal - firstVal).toFixed(1))
      : null;
  const isLoss = change !== null && change < 0;
  const isGain = change !== null && change > 0;

  const axisStyle  = { fill: isDark ? '#6b7280' : '#9ca3af', fontSize: 11 };
  const gridStroke = isDark ? '#1e293b' : '#f1f5f9';

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.15 }}
      className="mt-10"
      data-testid="progress-section"
    >
      {/* Section header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h3 className="font-semibold text-gray-900 dark:text-gray-100 text-lg">Моят прогрес</h3>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-0.5">
            {planCreatedAt
              ? `От ${new Date(planCreatedAt).toLocaleDateString('bg-BG', { day: 'numeric', month: 'long' })}`
              : 'Проследяване на измерванията ти'}
          </p>
        </div>
        <button
          data-testid="add-checkin-btn"
          onClick={onAddCheckin}
          className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl border border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-400 text-sm font-semibold hover:bg-emerald-50 dark:hover:bg-emerald-900/20 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Нов отчет
        </button>
      </div>

      {loading ? (
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-20 bg-gray-100 dark:bg-slate-800 rounded-2xl animate-pulse" />
            ))}
          </div>
          <div className="h-56 bg-gray-50 dark:bg-slate-800 rounded-2xl animate-pulse" />
        </div>
      ) : filteredHistory.length === 0 ? (
        /* Empty state */
        <Card className="rounded-2xl border border-dashed border-gray-200 dark:border-slate-700">
          <CardContent className="py-12 text-center">
            <Scale className="w-10 h-10 text-gray-300 dark:text-slate-600 mx-auto mb-3" />
            <p className="text-gray-500 dark:text-gray-400 text-sm font-medium">
              {planCreatedAt ? 'Все още няма отчети за този план' : 'Няма записани данни все още'}
            </p>
            <p className="text-gray-400 dark:text-slate-600 text-xs mt-1">
              Добави първия си отчет, за да започнеш да следиш прогреса си
            </p>
            <button
              data-testid="add-first-checkin-btn"
              onClick={onAddCheckin}
              className="mt-5 inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold transition-colors"
            >
              <Plus className="w-4 h-4" />
              Добави първи отчет
            </button>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Stats row — per active metric */}
          {activeHistory.length >= 2 && (
            <div className="grid grid-cols-3 gap-3 mb-5">
              <div className="bg-gray-50 dark:bg-slate-800 rounded-2xl p-3.5 text-center" data-testid="progress-start-weight">
                <p className="text-xs text-gray-400 dark:text-gray-500 mb-1">Начално</p>
                <p className="font-bold text-gray-900 dark:text-gray-100 text-lg tabular-nums">
                  {firstVal}
                  <span className="text-xs font-normal text-gray-400 dark:text-gray-500 ml-0.5">
                    {activeCfg.unit}
                  </span>
                </p>
              </div>
              <div className="bg-gray-50 dark:bg-slate-800 rounded-2xl p-3.5 text-center" data-testid="progress-current-weight">
                <p className="text-xs text-gray-400 dark:text-gray-500 mb-1">Текущо</p>
                <p className="font-bold text-gray-900 dark:text-gray-100 text-lg tabular-nums">
                  {lastVal}
                  <span className="text-xs font-normal text-gray-400 dark:text-gray-500 ml-0.5">
                    {activeCfg.unit}
                  </span>
                </p>
              </div>
              <div
                className={`rounded-2xl p-3.5 text-center ${
                  isLoss ? 'bg-emerald-50 dark:bg-emerald-900/20'
                  : isGain ? 'bg-orange-50 dark:bg-orange-900/20'
                  : 'bg-gray-50 dark:bg-slate-800'
                }`}
                data-testid="progress-weight-change"
              >
                <p className="text-xs text-gray-400 dark:text-gray-500 mb-1">Промяна</p>
                <p
                  className={`font-bold text-lg flex items-center justify-center gap-1 tabular-nums ${
                    isLoss ? 'text-emerald-600 dark:text-emerald-400'
                    : isGain ? 'text-orange-500 dark:text-orange-400'
                    : 'text-gray-900 dark:text-gray-100'
                  }`}
                >
                  {isLoss ? <TrendingDown className="w-4 h-4 flex-shrink-0" />
                   : isGain ? <TrendingUp className="w-4 h-4 flex-shrink-0" />
                   : <Minus className="w-4 h-4 flex-shrink-0" />}
                  {change !== null ? Math.abs(change) : '—'}
                  <span className="text-xs font-normal ml-0.5">{activeCfg.unit}</span>
                </p>
              </div>
            </div>
          )}

          {/* BMI + Prediction */}
          {filteredHistory.length >= 2 && <BmiPredictionCard history={filteredHistory} />}

          {/* Chart card */}
          <Card className="rounded-2xl border border-gray-100 dark:border-slate-700 overflow-hidden">
            <CardContent className="p-4 pt-5">
              {/* Metric tabs — only when more than 1 metric has data */}
              {availableTabs.length > 1 && (
                <div
                  className="flex gap-2 mb-5 flex-wrap"
                  data-testid="metric-tabs"
                >
                  {availableTabs.map((tabKey) => {
                    const cfg = METRIC_CONFIG[tabKey];
                    const isActive = activeTab === tabKey;
                    return (
                      <button
                        key={tabKey}
                        data-testid={`metric-tab-${tabKey}`}
                        onClick={() => setActiveTab(tabKey)}
                        className="flex-shrink-0 px-3.5 py-1.5 rounded-xl text-sm font-semibold transition-all duration-200 outline-none"
                        style={
                          isActive
                            ? {
                                backgroundColor: cfg.color,
                                color: '#fff',
                                boxShadow: `0 2px 10px ${cfg.color}55`,
                              }
                            : {
                                backgroundColor: isDark ? '#1e293b' : '#f1f5f9',
                                color: isDark ? '#94a3b8' : '#6b7280',
                              }
                        }
                      >
                        {cfg.label}
                      </button>
                    );
                  })}
                </div>
              )}

              {activeHistory.length < 2 ? (
                <div className="text-center py-8">
                  <p className="text-gray-400 dark:text-gray-500 text-sm">
                    Нужни са поне 2 записа, за да се покаже графиката
                  </p>
                  <p className="text-gray-300 dark:text-slate-600 text-xs mt-1">
                    Добави следващия отчет след 7 дни
                  </p>
                </div>
              ) : (
                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeTab}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    transition={{ duration: 0.2 }}
                  >
                    <ResponsiveContainer width="100%" height={230} data-testid="progress-chart">
                      <AreaChart
                        data={chartData}
                        margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
                      >
                        <defs>
                          <linearGradient id="metricGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%"  stopColor={activeCfg.color} stopOpacity={0.28} />
                            <stop offset="95%" stopColor={activeCfg.color} stopOpacity={0}    />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} vertical={false} />
                        <XAxis
                          dataKey="date"
                          tick={axisStyle}
                          axisLine={false}
                          tickLine={false}
                          dy={8}
                        />
                        <YAxis
                          tick={axisStyle}
                          axisLine={false}
                          tickLine={false}
                          domain={['auto', 'auto']}
                        />
                        <Tooltip content={<CustomTooltip unit={activeCfg.unit} />} />
                        <Area
                          type="monotone"
                          dataKey="value"
                          stroke={activeCfg.color}
                          strokeWidth={2.5}
                          fill="url(#metricGradient)"
                          dot={{
                            fill: activeCfg.color,
                            r: 4,
                            strokeWidth: 2,
                            stroke: isDark ? '#0f172a' : '#fff',
                          }}
                          activeDot={{
                            r: 6,
                            fill: activeCfg.color,
                            strokeWidth: 2,
                            stroke: isDark ? '#0f172a' : '#fff',
                          }}
                          isAnimationActive={false}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </motion.div>
                </AnimatePresence>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </motion.div>
  );
};
